/**
 * 放送地点の新規作成 / 編集モーダル。
 *
 * - 地点情報フォーム + コンテンツ編集を **多段組グリッド** (`.form-grid`) に配置する。
 *   3 段組時の並びは 1 段目「📍地図を開く / 緯度 / 経度 → 説明」・2 段目「半径 / 放送遅延」で、
 *   ユーザーは必ず地図から座標を指定するため、**地図ボタンを 1 段目の左端 (先頭)** に置き、
 *   「地図を開く → 緯度経度が決まる → 説明」という実際の操作順に並びを合わせている。

 *   画面幅に応じて 2 段 / 1 段へ自動追従して縦スクロール量を抑える。
 * - 放送遅延は「判定された GPS 圏内に入ってから何秒後に放送するか」であることが
 *   ラベルだけでは伝わらないため、入力欄の下に補足ヒントを表示する。
 * - モーダル自体のサイズは「大きすぎる」という指摘を受けて画面いっぱいをやめ、
 *   `min(1100px, 90vw)` / `max-height: 86vh` (styles.css `.modal--single-column`) に収める。

 * - 地図は「📍 地図を開く」で **モーダル本体と同サイズのポップアップ** として重ねて表示する
 *   (ピン + 半径円。ドラッグまたはクリックで位置指定)。
 *   地図で指定した座標は入力欄へ即時反映され、フッターの「この位置を使う」/ × / Esc の
 *   いずれで閉じても座標は保持される (保存は編集画面の「保存」ボタンで行う)。
 *   新規作成時は「新規作成」ボタン押下直後から地図を最前面に開き (裏に新規作成画面)、
 *   位置が未指定のうちは対象路線の全体が見える縮尺で表示する。
 * - モーダル背景 (`.modal-backdrop`) は背後の一覧テーブル / 地図をぼかして、
 *   編集作業に視線を集中させる。
 * - 半径スライダー操作で地図上の Circle がリアルタイムに同期する。
 * - spotId は UI 上に一切表示せず、編集対象を識別する内部値としてのみ保持する

 *   (新規作成時は undefined で、API 側が UUID v7 を採番する)。
 * - 1 地点に対して複数のコンテンツを設定可能。カテゴリ毎に必要な入力フィールドが
 *   動的に切り替わる。
 * - 保存ボタンで親に値を渡し、親側で API 呼び出し (POST/PUT) を実行する。
 */
import { Spinner } from './Spinner';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import type {
  BroadcastContent,
  BroadcastContentCategory,
  BroadcastPoint,
  Chime,
} from '../api/types';
import type { GtfsStop } from '../data/gtfsStops';
import type { IStationsLine } from '../data/istationsLines';

import {
  DEFAULT_LANGUAGE_KEY,
  POLLY_LANGUAGE_OPTIONS,
  voiceOptionsForLanguage,
} from '../data/pollyVoices';

import { getRouteColor, parseRouteDirection, type RouteMaster } from '../data/routes';
import {
  fetchRouteLine,
  fetchRouteShapePoints,
  type RouteLineFile,
  type RouteShapePointsFile,
} from '../data/routeShapes';
import { findStationSegment, formatStationSegmentLabel, type RouteStop } from '../data/routeStops';
import {
  createDefaultContent,
  deriveSpotEnabled,
  disableContentsOfDisabledSpot,
  validateContentIssues,
} from '../utils/broadcastContents';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useKaraokePlayback } from '../hooks/useKaraokePlayback';
import { fingerprint, type RemoteChangeState } from '../utils/remoteChange';
import { fillContentsRouteNicknames } from '../utils/routeNicknameDefaults';
import { ssmlToPlainText } from '../utils/karaoke';
import { playSequential, type SequentialAudioItem } from '../utils/sequentialAudio';
import { PointPreviewConditionDialog } from './PointPreviewConditionDialog';
import { PointPreviewDialog } from './PointPreviewDialog';
import type { RevealTarget, ValidationIssue } from '../utils/validationIssues';
import { firstIssueMessage, hasIssueForTestId, toRevealTarget } from '../utils/validationIssues';

import { BroadcastContentsEditor, type PreviewSsmlHandler } from './BroadcastContentsEditor';
import { usePreviewSample } from './broadcastContents/hooks/usePreviewSample';
import { buildPreviewQueue } from './broadcastContents/previewLanguages';
import { PreviewVoiceContext } from './broadcastContents/PreviewVoiceContext';
import { LocalizedVoiceFields } from './broadcastContents/parts/LocalizedVoiceFields';
import { type VoiceSelections } from '../utils/voiceSettings';
import { AddContentButtons } from './broadcastContents/parts/AddContentButtons';
import { PlaceholderHelpDialog } from './broadcastContents/parts/PlaceholderHelpDialog';
import { HintTooltip } from './HintTooltip';
import {
  buildPointSampleContext,
  derivePointSampleLimits,
  derivePointSampleOverrides,
  resolvePointSampleFields,
} from './broadcastContents/previewSample';
import { BroadcastPointsMap } from './BroadcastPointsMap';
// 数値入力は共通の NumericInput を使う (0 が消せない不具合の共通対策)。
import { clampNonNegativeInt, NumericInput } from './NumericInput';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';

export interface BroadcastPointFormValues {
  routeDirection: string;
  /**
   * 内部的にのみ保持する spotId。UI 上には表示されず、API への識別子として渡す。
   * 新規作成時は undefined。
   */
  spotId?: string;
  description: string;
  latitude: number | undefined;
  longitude: number | undefined;
  radius_meters: number;
  broadcast_delay_seconds: number;
  /**
   * 放送地点単位で切り替える Amazon Polly の VoiceId (日本語 Neural)。
   *
   * フォーム内では常に string で扱い (未設定は空文字)、保存時に親側で
   * 空文字→undefined に正規化する (BroadcastPointsPage.handleFormSubmit /
   * handleImport)。選択肢は `web-app/src/data/pollyVoices.ts` の
   * `POLLY_VOICE_OPTIONS` を参照。空文字はサーバ側デフォルト (`Takumi`) にフォールバック。
   *
   * 用途: Polly SSML 合成 (放送 / プレビュー) の声色切替。遅延判定等には影響しない。
   */
  voice_id: string;
  /**
   * 日本語以外の言語ごとの Polly VoiceId (`言語キー → VoiceId`) — Issue #639。
   *
   * 日本語の話者は {@link voice_id} が担う。空文字 / 未定義は「その言語の既定話者」。
   * 保存時に親側 (BroadcastPointsPage) が `voice_settings` へ変換する
   * (`web-app/src/utils/voiceSettings.ts` の `buildVoiceSettingsPayload`)。
   * 未指定のときは空辞書として扱う。
   */
  voice_by_language?: VoiceSelections;
  /**
   * 放送 **開始** 時に鳴らすチャイム音の `chime_id` (要件 3・4)。
   *
   * フォーム内では常に string で扱い (未設定 / 「(なし)」選択は空文字)、
   * 保存時に親側で空文字→undefined に正規化する
   * (BroadcastPointsPage.handleFormSubmit / handleImport)。
   *
   * 選択肢は ControlPanel API `GET /chimes` (チャイムマスタ) から取得した
   * {@link BroadcastPointFormProps.chimes} を利用する。空文字は「開始チャイムを
   * 鳴らさない」を意味する (既存挙動と完全に同一)。
   */
  start_chime_id: string;
  /**
   * 放送 **終了** 時に鳴らすチャイム音の `chime_id` (要件 3・4)。
   * 仕様は {@link start_chime_id} と同じ (差し込み位置が放送キューの末尾になる)。
   */
  end_chime_id: string;
  /**
   * 放送地点の有効 / 無効 (Issue #679)。
   *
   * 一覧のトグルで切り替えるほか、編集モーダルでは各コンテンツのトグル (Issue #810) と
   * 連動して自動で決まる: 全コンテンツが無効なら `false`、一つでも有効なら有効。
   * 地点だけが無効な既存データは、開いたときに全コンテンツを無効にそろえる
   * (`disableContentsOfDisabledSpot`)。
   * PUT は全置換のため、編集保存で無効状態が消えて勝手に有効へ戻らないよう、
   * 値を保持して送り返す。未指定 / `true` は有効。
   */
  enabled?: boolean;
  contents: BroadcastContent[];
}

export interface BroadcastPointFormProps {
  /** モーダルのタイトルに表示するモード */
  mode: 'create' | 'edit';
  /** 編集対象 (新規作成時は routeDirection のみ与える) */
  initialValue: BroadcastPointFormValues;
  /** 同 routeDirection の他の地点 (地図にグレーで表示) */
  otherPoints: BroadcastPoint[];
  /**
   * 親が既に取得済みの当該方面の線形 GeoJSON (Point Feature の集合)。
   * - 親側 (BroadcastPointsPage) で選択中の routeDirection に対応する GeoJSON が
   *   ここに渡される。`null` の場合、フォーム側で `initialValue.routeDirection`
   *   から自前で fetch する (= 全件 scan モードのテーブル経由で編集を開いたケース)。
   */
  routeShapePoints: RouteShapePointsFile | null;
  /**
   * `routeShapePoints` がどの routeDirection 用に取得されたものかを示す。
   * - 一覧の「複製」でコピー元と異なる線区/方面を選択した場合、
   *   親 (BroadcastPointsPage) は画面上で選択中の routeDirection に対応する
   *   `routeShapePoints` をそのまま渡してくるが、編集対象の routeDirection は
   *   既にコピー先のものになっている。
   * - そのため親値をそのまま地図に渡すと、コピー元路線の線形 (強調表示・GPS 強度・
   *   定型放送区間) が描画されてしまう。
   * - 本フィールドが `values.routeDirection` と一致しないときは、親値を採用せず
   *   フォーム側で自前 fetch した値 (`localShapePoints`) を使う。
   */
  routeShapePointsRouteDirection?: string;

  /**
   * 線区マスタ。接続線遅延コンテンツの route_id ドロップダウンに使用する。
   * 省略時は空配列扱い。
   */
  routes?: RouteMaster[];
  /**
   * 駅順マスタ (route_id → 駅一覧)。接続線遅延コンテンツの開始駅 / 終了駅ドロップダウン用。
   * 親 (BroadcastPointsPage) が ControlPanel API 経由で事前取得して渡す。省略時は空。
   */
  routeStops?: Record<string, RouteStop[]>;
  /**
   * 接続線遅延コンテンツの「運行情報の参照元（アイステイションズ路線）」複数選択用のマスタ。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   * 親 (BroadcastPointsPage) が `fetchIStationsLines()` で事前取得して渡す。
   */
  istationsLines?: IStationsLine[];
  /**
   * GTFS 静的 stops (親駅 + 子番線) の全件。任意テキストの放送条件「停車番線」の
   * 番線ドロップダウン用。`route_id` で絞り込めないため
   * 親 (BroadcastPointsPage) が `getGtfsStops()` で 1 回だけ全件取得して渡す。
   * 省略時は空配列として扱い、番線セレクトは無効化される (既存の選択値は残す)。
   */
  gtfsStops?: GtfsStop[];
  /**

   * チャイム音マスタ (`GET /chimes`) の一覧 (要件 3・4)。

   * 放送開始 / 終了チャイムのセレクタの選択肢として使う。省略時は空配列扱いで、
   * セレクタは「(なし)」のみとなる (= チャイム未設定)。
   */
  chimes?: Chime[];
  /**
   * 選択中チャイムの試聴処理 (`GET /chimes/{chime_id}/audio`) (要件 3・4)。
   * chime_id を受け取り、再生可能な object URL (または data URL) を返す。
   * 省略時は試聴ボタンを表示しない。
   */
  onPreviewChime?: (chimeId: string) => Promise<string>;
  /**
   * 任意テキスト (free_text) コンテンツの音声ファイルアップロード処理。
   * MP3 File を受け取り、保存された audio_key を返す。省略時はアップロード UI を無効化する。
   */
  onUploadAudio?: (file: File) => Promise<string>;
  /**
   * SSML / 任意テキストの音声プレビュー処理。省略時はプレビュー UI を無効化する。
   */
  onPreviewSsml?: PreviewSsmlHandler;
  /** 保存処理 (Promise を返す。throw でエラー表示) */
  onSubmit: (values: BroadcastPointFormValues) => Promise<void>;
  /**
   * 更新 (編集モード) の確定前に、開いてから他者がこの地点を更新 / 削除していないかを確認する関数 (Issue #765)。
   * サーバから最新の地点を取り直し、開いたときの内容と比べた結果を返す。省略時は確認しない。
   * 自分が何も変更していないときは呼ばれない。
   */
  onCheckRemoteState?: () => Promise<RemoteChangeState>;
  /**
   * 編集中に他者が削除していたとき、「他者によって削除されています」のお知らせを閉じた後に呼ばれる
   * (画面側でモーダルを閉じて一覧を取り直す)。省略時は `onClose` を呼ぶ。
   */
  onDeletedByOthers?: () => void;
  /** キャンセル */
  onClose: () => void;
}

const DEFAULT_RADIUS = 100;

/**
 * 半径 (m) のワンクリックプリセット。
 *
 * 現場 WG からの要望で「よく使う半径はスライダーを動かさずワンクリックで設定したい」ため、
 * スライダーの横にプリセットボタンを並べる (スライダーによる任意値指定も従来通り可能)。
 */
const RADIUS_PRESETS: readonly number[] = [100, 150, 200];

/**
 * 地図ポップアップの初期サイズ・位置のフォールバック値算出。
 *
 * 基本方針は「編集モーダル本体と同じ大きさ・同じ位置に重ねて表示する」。
 * モーダルの実寸は `getBoundingClientRect()` で取得するが、まだレイアウトが
 * 確定していない (jsdom など幅 0 になる) 場合はこのフォールバックを使う。
 *
 * NOTE: モジュールスコープで `window` を参照すると、テスト環境や
 * ウィンドウリサイズ後に古い値が固定されてしまうため、必ず呼び出し時に評価する。
 */
function fallbackPopupRect(): { width: number; height: number; top: number; left: number } {
  // モーダル本体 (.modal--single-column: width min(1100px, 90vw) / max-height 86vh)
  // と同等の見え方になる値を使う。
  const width = Math.min(1100, Math.round(window.innerWidth * 0.9));
  const height = Math.round(window.innerHeight * 0.86);

  return {
    width,
    height,
    top: Math.max(8, Math.round((window.innerHeight - height) / 2)),
    left: Math.max(8, Math.round((window.innerWidth - width) / 2)),
  };
}

export function BroadcastPointForm(props: BroadcastPointFormProps): React.JSX.Element {
  const {
    mode,
    initialValue,
    otherPoints,
    routeShapePoints,
    routeShapePointsRouteDirection,
    routes,
    routeStops,
    istationsLines,
    gtfsStops,
    chimes,

    onPreviewChime,
    onUploadAudio,
    onPreviewSsml,
    onSubmit,
    onCheckRemoteState,
    onDeletedByOthers,
    onClose,
  } = props;

  const [values, setValues] = useState<BroadcastPointFormValues>(() =>
    prepareValues(initialValue, routes),
  );
  /**
   * 開いたときの入力内容の指紋 (Issue #765)。編集時に「何か変更したか」を判定する基準。
   * 線区マスタ到着による自動補完など、利用者の操作ではない変化があったときは基準も一緒に更新する。
   */
  const [baseline, setBaseline] = useState<string>(() =>
    fingerprint(prepareValues(initialValue, routes)),
  );

  /*
   * 放送地点単位の設定 (言語別の話者選択など) を出すかの判定に使う
   * **放送言語の和集合**。
   *
   * 放送言語はコンテンツ単位 (`BroadcastContent.languages`) に持つ一方で、
   * 話者は **放送地点単位** の設定なので、いずれかのコンテンツで
   * 使われる言語をすべて入力対象にする必要がある。
   * コンテンツが 0 件 / 未指定の場合は従来と同じ「日本語のみ」として扱い、
   * 入力欄は表示しない (不要な欄で画面を埋めない)。
   */
  const spotLanguages = useMemo(() => {
    const keys = new Set<string>();
    for (const content of values.contents) {
      for (const language of content.languages ?? ['ja']) keys.add(language);
    }
    // マスタ定義順に揃える (入力欄の並びを放送順と一致させる)。
    return POLLY_LANGUAGE_OPTIONS.filter((option) => keys.has(option.key)).map(
      (option) => option.key,
    );
  }, [values.contents]);

  const [submitting, setSubmitting] = useState(false);
  /** 保存せずに閉じるときの破棄確認と、更新確定時の他者による更新 / 削除の確認 (Issue #765) */
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();
  /** 開いたときから入力内容を変更しているか (Issue #765) */
  const dirty = fingerprint(values) !== baseline;

  const [error, setError] = useState<string | null>(null);
  /**
   * 保存時に検出した入力エラーの一覧 (構造化済み)。
   *
   * フッターに「⚠ 入力エラーが N 件あります」のクリック可能なサマリとして表示し、
   * 各行のクリックで該当項目を開いてスクロールする。
   * 空配列 = エラー無し (サマリ非表示)。
   */
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  /**
   * 「隠れている項目を開いて見せる」ための指示。
   *
   * サマリの行クリック / 保存失敗時に更新し、子コンポーネント
   * (`BroadcastContentsEditor` → `ConnectingLineDelayByStationFields`
   *  → `ConnectingDirectionsSection`) が `useEffect` で自分の開閉 state を追従させる。
   * `nonce` を含めることで「同じ項目をもう一度開く」操作も伝わる (詳細は型の JSDoc)。
   */
  const [revealTarget, setRevealTarget] = useState<RevealTarget | null>(null);
  /** reveal 指示の連番。クリックごとに +1 して同一項目の再 reveal を可能にする。 */
  const revealNonceRef = useRef(0);
  /**
   * 地図ポップアップの表示状態。
   *
   * 新規作成時はユーザーが必ず地図から座標を指定するため、「新規作成」ボタン押下直後から
   * 地図を最前面に開いた状態で表示する (裏に新規作成画面が残る)。
   * ただし、地図の右クリック →「放送地点を新規作成」のように **座標が入った状態で開かれた場合は、
   * すでに地図で位置を選んでいるため自動では開かない** (再度の位置選択を求めない)。
   * 編集時は従来どおり閉じた状態で開始し、「📍 地図を開く」で表示する。
   */
  const [mapOpen, setMapOpen] = useState(shouldOpenMapOnStart(mode, initialValue));

  /** 「地図を開く」ボタンへの ref (ポップアップの位置計算に使う) */
  const mapButtonRef = useRef<HTMLButtonElement>(null);
  /**
   * モーダル本体（スクロール領域）への ref。
   * initialValue 切り替え時に scrollTop=0 へ戻して、
   * 切り替え前のスクロール位置が引き継がれるのを防ぐ。
   */
  const modalBodyRef = useRef<HTMLDivElement>(null);
  /** 右上「？」ボタンで開くプレースホルダのヘルプの表示状態。 */
  const [helpOpen, setHelpOpen] = useState(false);

  /**
   * チャイム試聴の状態 (要件 3・4)。
   * - `playing`: 現在試聴処理中の役割 (`'start'` / `'end'`)。多重クリック抑止に使う。
   * - `error`: 試聴に失敗した場合のメッセージ (保存処理のエラーとは分離して表示する)。
   */
  const [chimePreviewPlaying, setChimePreviewPlaying] = useState<'start' | 'end' | null>(null);
  const [chimePreviewError, setChimePreviewError] = useState<string | null>(null);

  /**
   * 放送地点まるごとプレビュー (通し聴き) の状態 (Issue #439)。
   *
   * - `playing`: 生成 + 連続再生中フラグ (多重クリック抑止)。
   * - `error`: 失敗メッセージ (保存エラーとは分離して表示し、保存操作をブロックしない)。
   * - `progress`: 「いま何を鳴らしているか」のラベル (例: 「#2 接続線遅延」)。
   */
  const [pointPreviewPlaying, setPointPreviewPlaying] = useState(false);
  const [pointPreviewError, setPointPreviewError] = useState<string | null>(null);
  const [pointPreviewProgress, setPointPreviewProgress] = useState<string | null>(null);
  const pointPreviewController = useRef<AbortController | null>(null);
  // Issue #643: 通し試聴中の「いま鳴っている文面」をカラオケ風に表示する。
  const pointKaraoke = useKaraokePlayback();
  // 通し試聴の前に開く「プレビュー条件」ダイアログの開閉。
  // 条件の値 (`pointSample`) は親が持つので、閉じても入力値は残る。
  const [pointConditionOpen, setPointConditionOpen] = useState(false);
  useEffect(() => () => pointPreviewController.current?.abort(), []);

  /*
   * 放送地点まるごとプレビューの「プレビュー条件」(サンプル条件) — Issue #439。
   *
   * 個別フィールドのプレビュー (`AudioPreviewField`) と **同じ共通部品**
   * (`usePreviewSample` + `PreviewSamplePanel`) を使う。これにより
   *
   *   - 遅延の分 ⇔ 秒 換算
   *   - 「未編集のキーだけコンテンツ設定へ追従し、手入力は上書きしない」規則
   *   - 入力欄の出し分け (本番で読み上げない項目は出さない)
   *
   * が個別プレビューと完全に一致し、「画面によって指定できる条件が違う」という
   * 分かりにくさが生じない。
   *
   * 自動導出値は全コンテンツから畳み込む (`derivePointSampleOverrides`) ため、
   * コンテンツを編集するとサンプル遅延・乗換駅・方面も追従する
   * (ユーザーが条件パネルで明示指定した項目はそのまま尊重される)。
   */
  const pointSampleFields = resolvePointSampleFields(values.contents);
  const pointSampleHasFields =
    pointSampleFields.showDelay ||
    pointSampleFields.showRouteName ||
    pointSampleFields.showFromStation ||
    pointSampleFields.showTransferStation ||
    pointSampleFields.showDirectionName ||
    pointSampleFields.showDepartureTime ||
    pointSampleFields.showHeadsign;
  /*
   * 通し試聴で指定できる入力枠 (Issue #445 / レビュー反映)。
   *
   * 「現在設定可能な項目に応じて設定できるように」という要望どおり、
   * 接続線・方面の入力欄は **地点内の全コンテンツの設定内容** から導出する
   * (`route_id` 単位でマージするため、同じ接続線が複数コンテンツにあっても 1 行)。
   */
  const pointSampleLimits = derivePointSampleLimits(values.contents);
  const pointSample = usePreviewSample({
    initial: () => buildPointSampleContext(values.contents),
    overrides: derivePointSampleOverrides(values.contents),
    limits: pointSampleLimits,
  });

  // ---------------------------------------------------------------------------
  // モーダルドラッグ
  // ---------------------------------------------------------------------------
  const [modalPos, setModalPos] = useState<{ top: number; left: number } | null>(null);
  const modalDragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleModalHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const el = modalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    modalDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect.left,
      origTop: rect.top,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!modalDragRef.current) return;
      const dx = e.clientX - modalDragRef.current.startX;
      const dy = e.clientY - modalDragRef.current.startY;
      setModalPos({
        left: Math.max(0, modalDragRef.current.origLeft + dx),
        top: Math.max(0, modalDragRef.current.origTop + dy),
      });
    };
    const handleMouseUp = (): void => {
      modalDragRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  /**
   * 説明欄が「駅間判定による自動入力値」かどうか。
   * 新規作成時のみ、地図クリックで自動入力した直後は true となり、
   * 続けて地図を動かした際に説明を上書きしてよいと判断する。
   * ユーザーが説明を手入力で変更したら false に戻し、以後は自動上書きしない。
   *
   * NOTE: state ではなく ref で保持する。
   * 自動入力ロジックは `setValues` の関数更新コールバック内で参照されるが、
   * state を使うとクロージャに古い値がキャプチャされ、地図ドラッグ等で
   * 連続して `handleLocationChange` が呼ばれた際に 2 回目以降の上書きが
   * 効かなくなる (1 回目の `setDescriptionAutoFilled(true)` がまだ反映
   * されていないため `descriptionAutoFilled` が false のまま、かつ
   * `prev.description` は既に駅間ラベルになっており空ではないため、
   * `canAutoFill` 判定で false になってしまう)。
   * ref であれば同期的に値を更新でき、同一レンダー内の連続呼び出しでも
   * 正しく上書きが継続される。
   */
  const descriptionAutoFilledRef = useRef(false);

  /**
   * 編集対象の routeDirection に対応する線形 GeoJSON。
   * 親側 (BroadcastPointsPage) が同じ routeDirection 用に取得済みなら親値を使い、
   * それ以外 (例: 全件 scan モードのテーブルから別 routeDirection の編集を開いた場合) は
   * モーダル側で自前で取得する。
   */
  const [localShapePoints, setLocalShapePoints] = useState<RouteShapePointsFile | null>(null);

  // initialValue が変わった (= 別の地点を編集対象に切り替えた、または
  // 一覧の「複製」から新規作成モーダルを開き直した) 場合のみリセット
  useEffect(() => {
    const prepared = prepareValues(initialValue, routes);
    // 地図の右クリック →「放送地点を新規作成」のように、座標が入った状態で新規作成を開いた場合も、
    // 地図クリック時と同様に駅間ラベル (「開始駅-終了駅」) を説明欄へ自動入力する。
    // (説明が空のときだけ。座標なしの新規作成 / 編集 / 複製では何もしない)
    const autoLabel =
      mode === 'create' && prepared.description.trim() === ''
        ? computeStationSegmentLabel(
            routeStops,
            prepared.routeDirection,
            prepared.latitude,
            prepared.longitude,
          )
        : null;
    const nextValues = autoLabel ? { ...prepared, description: autoLabel } : prepared;
    setBaseline(fingerprint(nextValues));
    setValues(nextValues);
    setError(null);
    // 自動入力した場合は、続けて座標を動かしたときも説明を追従させられるよう記録する。
    descriptionAutoFilledRef.current = autoLabel !== null;
    // 「複製」から新規作成モードとして開かれた場合も、
    // 新規作成ボタン押下時と同様に地図を最前面に開く。
    // ただし座標が入っている (地図の右クリックから開いた) 場合は開かない。
    if (mode === 'create') setMapOpen(shouldOpenMapOnStart(mode, initialValue));

    // モーダル本体のスクロール位置を先頭に戻す。
    // モーダル DOM が再利用される (アンマウントされない) ケースでは、
    // ブラウザがスクロール位置を保持してしまうため明示的にリセットする。
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
    // routes は「線区マスタが後から読み込まれた」ケースを下の effect が担うため依存に含めない
    // (含めると、マスタの再取得のたびに編集中の入力がリセットされる)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, mode]);

  /*
   * 線区マスタが後から届いた場合も、接続線の線区愛称の空欄をマスタの名称で埋める。
   *
   * 画面を開いた時点で `routes` が空 (読み込み中) のことがあるため。
   * 埋めるのは空欄だけで、手入力した値は上書きしない。ユーザーが意図して空にした欄は、
   * この effect が `routes` の変化でしか動かないので、編集中に勝手に戻ることはない。
   */
  useEffect(() => {
    setValues((v) => {
      const contents = fillContentsRouteNicknames(v.contents, routes);
      return contents === v.contents ? v : { ...v, contents };
    });
    // 利用者がまだ何も変更していないなら、自動補完は「変更」として扱わない (Issue #765)。
    // (基準も同じ補完を通しておく。補完は空欄だけを埋め、手入力は上書きしないので冪等。)
    setBaseline((b) => {
      try {
        const parsed = JSON.parse(b) as BroadcastPointFormValues;
        const contents = fillContentsRouteNicknames(parsed.contents ?? [], routes);
        return fingerprint({ ...parsed, contents });
      } catch {
        return b;
      }
    });
  }, [routes]);

  /**
   * 親から渡された `routeShapePoints` が現在の編集対象 `values.routeDirection` と
   * 一致するかどうか。
   *
   * - 一致する場合 (= 通常の編集 / 同方面新規作成): 親値をそのまま採用する。
   * - 一致しない場合 (= 一覧の「複製」でコピー元と異なる線区/方面を選択した
   *   ケースなど): 親値は採用せず、フォーム側で自前 fetch した `localShapePoints` を使う。
   * - `routeShapePointsRouteDirection` が undefined のときは互換のため「親値があれば
   *   一致扱い」とする (props を渡してこない既存呼び出し元向け)。
   */
  const parentShapeMatches =
    routeShapePointsRouteDirection === undefined ||
    routeShapePointsRouteDirection === values.routeDirection;

  // 親から線形が渡されていない、または親値が現在の routeDirection と
  // 一致しない場合は、編集対象の routeDirection で自前 fetch する。
  useEffect(() => {
    if (routeShapePoints && parentShapeMatches) {
      // 親値をそのまま使うのでローカル取得値は不要
      setLocalShapePoints(null);
      return;
    }
    const { routeId, directionName } = parseRouteDirection(values.routeDirection);
    if (!routeId || !directionName) {
      setLocalShapePoints(null);
      return;
    }
    let aborted = false;
    void (async () => {
      const data = await fetchRouteShapePoints(routeId, directionName);
      if (!aborted) setLocalShapePoints(data);
    })();
    return () => {
      aborted = true;
    };
  }, [routeShapePoints, parentShapeMatches, values.routeDirection]);

  // 親値が現在の routeDirection 用なら優先、そうでなければローカル取得値を地図に渡す。
  const effectiveShapePoints = parentShapeMatches
    ? (routeShapePoints ?? localShapePoints)
    : localShapePoints;

  /**
   * 編集対象線区の路線線形 (LineString。例: 横浜線) と、その線区カラー。
   * 地図ポップアップで路線を線区カラーで描くために使う。
   * 「複製」で線区が変わっても追従するよう、編集対象の route_id で取得する。
   * 地図を開いたときだけ取得し、route_id 付きで保持して取り違えを防ぐ。
   */
  const editingRouteId = parseRouteDirection(values.routeDirection).routeId;
  const [routeLine, setRouteLine] = useState<{ routeId: string; file: RouteLineFile } | null>(null);
  useEffect(() => {
    if (!mapOpen || !editingRouteId) return;
    let aborted = false;
    void (async () => {
      const file = await fetchRouteLine(editingRouteId);
      if (!aborted) setRouteLine(file ? { routeId: editingRouteId, file } : null);
    })();
    return () => {
      aborted = true;
    };
  }, [mapOpen, editingRouteId]);
  const visibleRouteLine = routeLine?.routeId === editingRouteId ? routeLine.file : null;
  const routeLineColor = getRouteColor(routes, editingRouteId).background;

  /**
   * 緯度経度から駅間ラベルを判定して、新規作成時に説明欄へ自動入力するための共通処理。
   *
   * - 自動入力は新規作成時 (`mode === 'create'`) のみ。
   * - 説明が空 または 直前に自動入力した値の場合のみ上書きする (手入力は尊重)。
   * - 緯度経度が未確定 / 不正な場合は何もしない。
   *
   * 引数の values から次状態を返し、必要に応じて descriptionAutoFilled も更新する。
   */
  const applyAutoDescription = (
    next: BroadcastPointFormValues,
    prev: BroadcastPointFormValues,
  ): BroadcastPointFormValues => {
    if (mode !== 'create') return next;
    // 説明が空 または 自動入力済みの場合のみ上書き対象とする (手入力は尊重)。
    // ref を使うことで、`setValues` 関数更新コールバック内でも常に最新の
    // 「直前に自動入力したか」を参照できる (state クロージャ問題を回避)。
    const canAutoFill = descriptionAutoFilledRef.current || prev.description.trim() === '';
    if (!canAutoFill) return next;

    const label = computeStationSegmentLabel(
      routeStops,
      prev.routeDirection,
      next.latitude,
      next.longitude,
    );
    if (!label) return next;

    // 自動入力したことを記録し、続けて緯度経度を変更しても上書きできるようにする。
    descriptionAutoFilledRef.current = true;
    return { ...next, description: label };
  };

  const handleChange = <K extends keyof BroadcastPointFormValues>(
    key: K,
    value: BroadcastPointFormValues[K],
  ): void => {
    // 説明をユーザーが手入力で変更したら、以後は自動上書きを止める。
    if (key === 'description') {
      descriptionAutoFilledRef.current = false;
    }
    setValues((v) => {
      const next = { ...v, [key]: value };
      // 緯度 / 経度が変わった場合は駅間自動入力を試みる (新規作成時のみ)。
      if (key === 'latitude' || key === 'longitude') {
        return applyAutoDescription(next, v);
      }
      return next;
    });
  };

  /**
   * カテゴリ別の既定値でコンテンツを末尾に追加する。
   * 見出し行の追加ボタンとフッターの追加ボタンで共通に使う。
   */
  const handleAddContent = (category: BroadcastContentCategory): void => {
    setValues((v) =>
      withSyncedSpotEnabled({
        ...v,
        contents: [...v.contents, createDefaultContent(category)],
      }),
    );
  };

  /**
   * 地図上で位置が指定された (クリック / ドラッグ) ときのハンドラ。
   *
   * - 緯度経度を常に更新する。
   * - 新規作成時 (`mode === 'create'`) かつ、説明欄が空 または 直前に自動入力した値の
   *   場合のみ、駅順マスタから「開始駅-終了駅」を判定して説明欄へ自動セットする。
   *   ユーザーが手入力した説明は尊重して上書きしない。
   */
  const handleLocationChange = (lat: number, lng: number): void => {
    setValues((v) => {
      const next = { ...v, latitude: lat, longitude: lng };
      return applyAutoDescription(next, v);
    });
  };

  /**
   * 選択中のチャイムを試聴する (要件 3・4)。
   *
   * `onPreviewChime` (= `GET /chimes/{chime_id}/audio`) で取得した再生用 URL を
   * `Audio` 要素で再生する。試聴失敗は本フォームの保存エラーとは別枠で表示し、
   * 保存操作をブロックしない (チャイム設定そのものは選択済みの chime_id で成立する)。
   */
  const handlePreviewChime = async (role: 'start' | 'end'): Promise<void> => {
    if (!onPreviewChime) return;
    const chimeId = role === 'start' ? values.start_chime_id : values.end_chime_id;
    if (!chimeId) return;
    setChimePreviewError(null);
    setChimePreviewPlaying(role);
    try {
      const url = await onPreviewChime(chimeId);
      const audio = new Audio(url);
      await audio.play();
    } catch (err) {
      setChimePreviewError(describeApiErrorMessage(err, 'チャイムの試聴に失敗しました'));
    } finally {
      setChimePreviewPlaying(null);
    }
  };

  /**
   * 放送地点まるごとプレビュー (通し聴き) — Issue #439。
   *
   * 実際の放送キュー順に音声を生成してから連続再生する。
   *
   *   1. 開始チャイム (`start_chime_id` → `GET /chimes/{chime_id}/audio`)
   *   2. `contents[]` を並び順に (各コンテンツの `playback_rate` を要素ごとに反映)
   *   3. 終了チャイム (`end_chime_id`)
   *
   * サーバ側で ffmpeg 結合する必要はなく、`<audio>` の `ended` を繋ぐ
   * プレイリスト再生 (`playSequential`) で十分。
   *
   * 条件付きコンテンツ (`conditions` 付きの free_text や遅延しきい値未満のもの) は
   * 「条件を満たしたものとして鳴らす」扱いにする (サーバ側のサンプル条件で合成される)。
   * 生成に失敗したコンテンツはスキップし、最後にまとめて警告する
   * (1 件の設定ミスで通し聴き全体が不可能になるのを避けるため)。
   */
  const handlePreviewWholePoint = async (): Promise<void> => {
    if (!onPreviewSsml || pointPreviewController.current) return;
    const controller = new AbortController();
    pointPreviewController.current = controller;
    setPointPreviewError(null);
    setPointPreviewPlaying(true);
    setPointPreviewProgress('音声を生成しています...');
    const skipped: string[] = [];
    // 「プレビュー条件」パネルで指定した (または未指定なら自動導出した) サンプル条件。
    // 個別プレビューと同じ解決規則 (`usePreviewSample`) を通るため、
    // 「条件パネルに出ている値」と「実際に鳴る音声」が食い違わない。
    const sampleContext = pointSample.resolve();
    const items: SequentialAudioItem[] = [];
    try {
      // 1. 開始チャイム
      if (values.start_chime_id && onPreviewChime) {
        try {
          items.push(
            pointKaraoke.attach({
              url: await onPreviewChime(values.start_chime_id),
              label: '開始チャイム',
            }),
          );
        } catch {
          skipped.push('開始チャイム');
        }
      }

      // 2. コンテンツ本体 (並び順)
      for (const { content, language, voiceId, label } of buildPreviewQueue(
        values.contents,
        values.voice_id,
        values.voice_by_language,
      )) {
        controller.signal.throwIfAborted();
        try {
          const result = await onPreviewSsml({
            content,
            language,
            voiceId,
            // Issue #439: 通し試聴でもプレビュー条件 (サンプル遅延・線名・乗換駅・方面) を
            // サーバへ送る。これが無いとサーバ側の既定サンプル (5 分固定) で鳴ってしまい、
            // 「境界値でどのパターンが鳴るか」を通しで確認できない。
            sampleContext,
          });
          items.push(
            pointKaraoke.attach({
              url: result.url,
              // 端末側と同じく、要素ごとに再生速度を適用する。
              playbackRate: content.playback_rate,
              label,
              // Issue #643: サーバがレンダリングした文面 (タグ除去済み) をカラオケ表示する。
              text: ssmlToPlainText(result.renderedSsml),
            }),
          );
        } catch {
          // 必須項目未入力 / サンプル遅延に該当パターン無しなどは 400 になる。
          // 通し聴き自体は続行し、最後にどれを飛ばしたか伝える。
          skipped.push(label);
        }
      }

      // 3. 終了チャイム
      controller.signal.throwIfAborted();
      if (values.end_chime_id && onPreviewChime) {
        try {
          items.push(
            pointKaraoke.attach({
              url: await onPreviewChime(values.end_chime_id),
              label: '終了チャイム',
            }),
          );
        } catch {
          skipped.push('終了チャイム');
        }
      }

      controller.signal.throwIfAborted();
      if (items.length === 0) {
        setPointPreviewError('通しで試聴できる放送内容がありません。設定を確認してください。');
        return;
      }

      setPointPreviewProgress(`${items.length} 件を順番に再生します...`);
      await playSequential(items, undefined, controller.signal);
      if (skipped.length > 0) {
        setPointPreviewError(
          `一部を試聴できませんでした (設定を確認してください): ${skipped.join(', ')}`,
        );
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setPointPreviewError(describeApiErrorMessage(err, '通し試聴に失敗しました'));
      }
    } finally {
      items.forEach((item) => {
        if (item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      });
      pointPreviewController.current = null;
      pointKaraoke.clear();
      setPointPreviewPlaying(false);
      setPointPreviewProgress(null);
    }
  };

  /**
   * エラーサマリの項目に対応する入力欄を「開いて・見せて・光らせる」。
   *
   * 1. `revealTarget` を更新して子コンポーネント側の折りたたみ / タブを開かせる。
   * 2. 開いた結果 DOM に現れる入力欄を `data-testid` で探し、
   *    スクロール (`block: 'center'`) + フォーカスする。
   *
   * ## なぜ setTimeout(0) が必要か
   *
   * `setRevealTarget` による再レンダー (= アコーディオン展開 / タブ切替) が
   * 反映される前に `querySelector` しても、対象要素はまだ DOM に存在しない
   * (折りたたみ中のコンテンツは `{open ? ... : null}` で描画されていない)。
   * マクロタスクへ逃がすことで「React のコミット後」に確実に探索できる。
   *
   * 対象 testId が解決できない (= 入力欄が存在しないエラー) 場合は、
   * コンテンツカード (`content-row-{index}`) までのスクロールにフォールバックする。
   */
  const revealIssue = (issue: ValidationIssue): void => {
    revealNonceRef.current += 1;
    setRevealTarget(toRevealTarget(issue.target, revealNonceRef.current));

    const { testId, contentIndex } = issue.target;
    setTimeout(() => {
      const root = modalBodyRef.current;
      if (!root) return;
      const selector =
        testId !== undefined
          ? `[data-testid="${testId}"]`
          : contentIndex !== undefined
            ? `[data-testid="content-row-${contentIndex}"]`
            : null;
      if (selector === null) return;
      const el = root.querySelector<HTMLElement>(selector);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // フォーカス可能な要素 (input / select / textarea / button) のときだけ focus する。
      // カード等の <div> に focus() しても効果がないため、無駄な副作用を避ける。
      if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    }, 0);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    // バリデーション: 全件収集して「どこを直せばよいか」まで案内する。
    const validationIssues = validateIssues(values);
    if (validationIssues.length > 0) {
      setIssues(validationIssues);
      // フッターの従来 1 行表示 (既存テスト / E2E 互換) も先頭 issue で維持する。
      setError(firstIssueMessage(validationIssues));
      // クリックを待たず、まず先頭のエラー箇所へ自動ジャンプする
      // (「押したのに何も起きない」体験を避けるため)。
      const first = validationIssues[0];
      if (first) revealIssue(first);
      return;
    }

    // 保存できる状態になったのでサマリを閉じる。
    setIssues([]);
    // 登録 / 更新に「実行してよろしいですか」は出さない (Issue #765)。
    // 更新 (編集) は、開いてから他者が更新 / 削除していないかを確認してから保存する。
    // 自分が何も変更していなければ、他者の更新 / 削除の確認は不要。
    // 確認中に入力が変わらない (モーダルで操作を塞ぐ) ため、ここで検証した values をそのまま保存する。
    const submit = (): void => {
      void (async () => {
        setSubmitting(true);
        try {
          await onSubmit(values);
        } catch (err) {
          setError(describeApiErrorMessage(err, '保存に失敗しました'));
        } finally {
          setSubmitting(false);
        }
      })();
    };
    if (mode !== 'edit' || !onCheckRemoteState) {
      submit();
      return;
    }
    await guardedUpdate({
      hasChanges: dirty,
      fetchRemoteState: onCheckRemoteState,
      execute: submit,
      onDeleted: onDeletedByOthers ?? onClose,
    });
  };

  /**
   * 閉じる (キャンセル / ×): 登録 / 更新せずに閉じるので破棄の確認を挟む (Issue #765)。
   * 新規は常に確認する。編集は、開いたときから何か変更したときだけ確認する。
   */
  const handleClose = (): void => {
    if (mode === 'create' || dirty) {
      confirmDiscard(onClose);
      return;
    }
    onClose();
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      data-testid="broadcast-point-form-modal"
      data-mode={mode}
    >
      <div
        ref={modalRef}
        className="modal modal--single-column"
        style={
          modalPos
            ? { position: 'fixed', top: modalPos.top, left: modalPos.left, transform: 'none' }
            : undefined
        }
      >
        <div
          className="modal-header"
          onMouseDown={handleModalHeaderMouseDown}
          style={{ cursor: 'grab' }}
        >
          <h2 style={{ margin: 0, fontSize: 15 }}>
            {mode === 'create' ? '放送地点を新規作成' : '放送地点を編集'} ({values.routeDirection})
          </h2>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              data-testid="modal-help-button"
              onClick={() => setHelpOpen(true)}
              aria-label="ヘルプ (利用できるプレースホルダ)"
              title="SSML テンプレートで利用できるプレースホルダを表示します"
            >
              ？
            </button>
            <button
              type="button"
              data-testid="modal-close-button"
              onClick={handleClose}
              disabled={submitting}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>
        {/*
          noValidate: ブラウザ標準の検証 (`required` の吹き出しと該当欄へのジャンプ) を無効にする。
          入力エラーの通知は、フッターのエラー一覧に統一するため (Issue #882)。
        */}
        <form onSubmit={handleSubmit} noValidate style={{ display: 'contents' }}>
          <div ref={modalBodyRef} className="modal-body modal-body--single-column">
            <div className="form-pane">
              <p className="form-required-hint">
                <span className="required-mark">必須</span> がついた項目は必ず入力してください
              </p>
              {/*
                入力欄の多段組レイアウト。
                以前は全項目が 1 行フル幅を占有しており、緯度・半径・放送遅延など
                短い値の欄まで極端に横長になっていた。入力内容の長さに応じて
                意味のまとまり単位で 1 セル (または複数セル) へ振り分ける。

                現場 WG 要望による段組の並び (3 段組時):
                  1 段目: 📍 地図を開くボタン / 緯度 / 経度 → 説明
                  2 段目: 半径 / 放送遅延

                ユーザーは必ず地図から緯度経度を指定するため、
                「地図を開く → 緯度経度が決まる → (自動入力される) 説明」の操作順に合わせて
                **緯度・経度セルを説明より前** に置き、**地図ボタンをセル内の左端** に置く。
                DOM 順で並べているので Tab 順・読み上げ順もこの操作順と一致する。

                地図ボタン + 位置の状態表示は 1 つの `.form-grid__inline` セルにまとめて 1 列分だけを占有する
                (緯度・経度の入力欄が無いので、2 列分の幅を取ると余白が出る)。

                列数は `.form-grid` の CSS 側で画面幅に応じて 2 段 / 3 段へ自動追従し、
                縦スクロール量を抑える。

                「どの項目がどの項目と紐づいているのか分かりにくい」という指摘を受けて、
                各セル (= 1 つの設定のまとまり) に `.form-grid__group` を付けて
                背景を薄いグレーで塗り分ける。
                例: 「緯度 / 経度 / 📍地図を開く」や「半径スライダー / 半径プリセット」は
                それぞれ 1 つの設定なので、同じグレーの枠の中に収まって見える。
              */}
              <div className="form-grid" data-testid="broadcast-point-form-grid">
                {/*
                  緯度・経度 + 地図ボタン (セル内で横並び)。

                  ユーザーは必ず地図から座標を指定するため、**説明より前 (1 段目の先頭)** に置き、
                  さらにセル内では **「📍 地図を開く」ボタンを左端** に配置する
                  (「まず地図を開く → 緯度経度が決まる」という操作順そのままの並び)。
                */}
                <div className="form-grid__inline form-grid__group form-grid__row-match">
                  {/*
                    見出し行。座標は必ず地図から指定するため、他のセルと同じ「必須」バッジを付ける
                    (`.field-heading > span.required::after`)。ボタンと状態表示はこの下の行に並ぶ。
                  */}
                  <span
                    className="field-heading form-grid__heading"
                    data-testid="map-field-heading"
                  >
                    <span className="required">地図</span>
                  </span>
                  {/*
                    地図を開くボタン。
                    緯度・経度の入力欄より前 (左端) に置き、入力欄と下端を揃える
                    (`.form-grid__inline` の `align-items: flex-end` と同じ効果)。
                  */}
                  <button
                    type="button"
                    ref={mapButtonRef}
                    className={mapOpen ? 'primary' : ''}
                    data-testid="map-open-button"
                    aria-label="地図で位置を選択"
                    aria-expanded={mapOpen}
                    onClick={() => setMapOpen((o) => !o)}
                    style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                  >
                    📍 地図{mapOpen ? 'を閉じる' : 'を開く'}
                  </button>
                  {/*
                    緯度・経度の数値入力欄は表示しない (座標は必ず地図から指定する運用のため)。
                    値そのものは `values.latitude` / `values.longitude` に保持され、保存・
                    駅間の自動入力・地図の初期位置には従来どおり使われる。
                    代わりに、位置が指定済みかどうかだけをここに表示する
                    (未指定のまま保存しようとしたときは、この枠と地図ボタンが赤枠になる)。
                  */}
                  <span
                    className={`map-location-status${
                      hasIssueForTestId(issues, 'map-open-button')
                        ? ' map-location-status--invalid'
                        : ''
                    }`}
                    data-testid="map-location-status"
                  >
                    {values.latitude === undefined ||
                    values.longitude === undefined ||
                    Number.isNaN(values.latitude) ||
                    Number.isNaN(values.longitude)
                      ? '未指定（地図で指定してください）'
                      : '指定済み'}
                  </span>
                </div>

                {/*
                  説明。
                  新規作成時は地図で座標を決めた時点で駅間ラベル (例: 代々木-新宿) が
                  自動入力されるため、緯度・経度セルの **後ろ** に配置している
                  (手入力した説明は自動上書きしない)。
                */}
                <label className="form-grid__group form-grid__row-match">
                  <span className="required">説明</span>
                  <input
                    type="text"
                    data-testid="description-input"
                    aria-label="説明"
                    // 保存時に検出したエラーの該当欄を赤枠で強調する。
                    // 入力し直して再保存すると `issues` が更新されるので自動で解除される。
                    className={hasIssueForTestId(issues, 'description-input') ? 'is-invalid' : ''}
                    value={values.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="例: 放送位置(代々木→新宿駅)"
                    required
                  />
                </label>

                {/*
                  Polly VoiceId (音声) セレクタ。放送 SSML 合成の声色を放送地点単位で
                  切り替えるための選択項目。未選択 (空文字) はサーバー側デフォルト
                  (Takumi) にフォールバックする。
                  選択肢は `web-app/src/data/pollyVoices.ts` の POLLY_VOICE_OPTIONS を参照。

                  プレースホルダ (value="") のラベルには具体的な声色名 (Takumi) を
                  出さない。以前は `(デフォルト: Takumi)` としていたが、その下に
                  `Takumi（男性・日本語 Neural）` の option も並ぶため、ユーザーから
                  見ると「Takumi 相当の選択肢が 2 つある」ように見えて不自然だった。
                  「未選択 = 既定音声にフォールバック」というセマンティクスを保ちつつ、
                  具体的な既定音声の名称は下のヒント文にのみ表示する。
                */}
                <label className="form-grid__group form-grid__row-match">
                  <span>
                    音声（任意）
                    <HintTooltip label="音声の説明" testId="voice-id-hint">
                      この放送地点の **日本語** の Polly 合成音声 (声色) を切り替えられます。
                      未選択の場合は既定の音声 (Takumi) を使用します。
                      日本語以外の言語は、コンテンツで選択した言語ごとに下の「各言語の音声」で選べます。
                    </HintTooltip>
                  </span>
                  <select
                    data-testid="voice-id-select"
                    aria-label="音声"
                    value={values.voice_id}
                    onChange={(e) => handleChange('voice_id', e.target.value)}
                  >
                    <option value="">（未選択 / 既定音声を使用）</option>
                    {voiceOptionsForLanguage(DEFAULT_LANGUAGE_KEY).map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                {/*
                  Issue #639: 日本語以外の放送言語ごとの音声 (Polly VoiceId) セレクタ。
                  いずれかのコンテンツで日本語以外の言語が選ばれているときのみ表示する
                  (日本語のみなら従来の画面と完全に同じ)。
                */}
                <LocalizedVoiceFields
                  languages={spotLanguages}
                  value={values.voice_by_language}
                  onChange={(value) => handleChange('voice_by_language', value)}
                />

                <label className="form-grid__group">
                  <span className="required">半径 (m): {values.radius_meters}</span>
                  <input
                    type="range"
                    min={10}
                    max={1000}
                    step={5}
                    data-testid="radius-input"
                    aria-label="半径"
                    value={values.radius_meters}
                    onChange={(e) => handleChange('radius_meters', Number(e.target.value))}
                  />
                  {/*
                    よく使う半径のワンクリックプリセット (現場 WG 要望)。
                    スライダーでの任意値指定も従来どおり可能で、現在値と一致するボタンは
                    `is-active` クラスでハイライトする。
                  */}
                  <div className="radius-presets" data-testid="radius-presets">
                    {RADIUS_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className={`radius-preset${values.radius_meters === preset ? ' is-active' : ''}`}
                        data-testid={`radius-preset-${preset}`}
                        aria-label={`半径 ${preset}m を設定`}
                        aria-pressed={values.radius_meters === preset}
                        onClick={() => handleChange('radius_meters', preset)}
                      >
                        {preset}m
                      </button>
                    ))}
                  </div>
                </label>

                <label className="form-grid__group">
                  <span className="required">
                    放送遅延 (秒)
                    <HintTooltip label="放送遅延の説明" testId="broadcast-delay-hint">
                      地図で指定した位置と半径で判定される GPS 圏内に入ったあと、
                      何秒後に放送を流すかを設定できます。0
                      の場合は圏内に入った時点で即時放送します。
                    </HintTooltip>
                  </span>
                  {/*
                    NumericInput が内部で「入力中の文字列」を保持し、一時的な空欄を許容する。
                    素の <input type="number" value={number}> だと 0 を消した瞬間
                    Number('') === 0 で 0 が復活してしまい「0 が消せない」不具合になる。
                    空欄のまま blur した場合は 0 表示に戻って保存値と一致する。
                  */}
                  <NumericInput
                    min={0}
                    step={1}
                    className="input--numeric"
                    data-testid="broadcast-delay-input"
                    aria-label="放送遅延"
                    value={values.broadcast_delay_seconds}
                    normalizeValue={clampNonNegativeInt}
                    onChange={(delaySeconds) =>
                      handleChange('broadcast_delay_seconds', delaySeconds)
                    }
                  />
                </label>

                {/*
                  要件 3・4: 放送開始 / 終了チャイム音のセレクタ (放送地点単位)。

                  - 選択肢はチャイムマスタ (`GET /chimes`) の一覧。「(なし)」を選ぶと
                    そのタイミングではチャイムを鳴らさない (= 既存挙動と完全に同じ)。
                  - サーバー側 (UpdateContents) は選択されたチャイム音源を
                    `contents_schedule[]` の先頭 / 末尾に `is_chime: true` エントリとして
                    差し込む。既存音源との合成 (ffmpeg) は行わない。
                  - 選択済みの場合は「▶ 試聴」ボタンで音を確認できる
                    (`GET /chimes/{chime_id}/audio`)。
                */}
                {/*
                  開始 / 終了チャイムは最初から 2 つのセレクトを並べて表示する。
                  保存データは `start_chime_id` / `end_chime_id` の 2 値 (バックエンドは変更しない)。
                */}
                <ChimeFields
                  startChimeId={values.start_chime_id}
                  endChimeId={values.end_chime_id}
                  chimes={chimes ?? []}
                  previewing={chimePreviewPlaying}
                  canPreview={Boolean(onPreviewChime)}
                  onStartChange={(chimeId) => handleChange('start_chime_id', chimeId)}
                  onEndChange={(chimeId) => handleChange('end_chime_id', chimeId)}
                  onPreview={(role) => void handlePreviewChime(role)}
                />
              </div>

              {chimePreviewError ? (
                <div
                  className="error-message"
                  role="alert"
                  data-testid="chime-preview-error"
                  style={{ marginTop: 8 }}
                >
                  {chimePreviewError}
                </div>
              ) : null}

              <PreviewVoiceContext.Provider
                value={values.voice_by_language ?? EMPTY_VOICE_SELECTIONS}
              >
                <BroadcastContentsEditor
                  contents={values.contents}
                  routes={routes}
                  routeStops={routeStops}
                  istationsLines={istationsLines}
                  // 任意テキストの放送条件「停車番線」の番線ドロップダウン用の GTFS stops。
                  // route_id では絞り込めないので、親ページが取得した全件をそのまま流す。
                  gtfsStops={gtfsStops}
                  // 自路線 (この地点が属する route_id)。BroadcastContentsEditor の

                  // 「接続線遅延 (乗換駅別)」の乗換駅ドロップダウンで、親路線の
                  // 駅一覧のみを候補として表示するために使う。
                  // (routeStops は全 route_id ぶんが Record で入っているので、
                  //  自路線に絞り込むにはこの値が必要)
                  parentRouteId={parseRouteDirection(values.routeDirection).routeId}
                  // 音声プレビュー (POST /audio-preview) に流し込む Polly VoiceId。
                  // 空文字はサーバー側デフォルト (Takumi) にフォールバックする。
                  parentVoiceId={values.voice_id || undefined}
                  // 入力エラーの「見つけやすさ」対応:
                  // - issues: カード見出しのエラーバッジ / 該当欄の赤枠に使う
                  // - revealTarget: 折りたたみ・タブを自動で開くための指示
                  issues={issues}
                  revealTarget={revealTarget}
                  onUploadAudio={onUploadAudio}
                  onPreviewSsml={onPreviewSsml}
                  onChange={(contents) =>
                    // Issue #810: コンテンツの有効 / 無効を地点の enabled へ連動させる
                    setValues((v) => withSyncedSpotEnabled({ ...v, contents }))
                  }
                  // 見出し行 (「コンテンツ (N 件)」の右) にもフッターと同じ追加ボタンを置く。
                  headerActions={<AddContentButtons variant="header" onAdd={handleAddContent} />}
                />
              </PreviewVoiceContext.Provider>

              {/*
                放送地点まるごとプレビュー (通し聴き) のボタンはフッターへ移動した
                (`footer-point-preview`)。コンテンツが増えてもスクロールで見えなくならない。
              */}
            </div>
          </div>
          <div className="modal-footer modal-footer--with-issues">
            {/*
              フッター左側の領域。上段にコンテンツ追加ボタン + 通し試聴ボタン、
              その下にエラー一覧を縦に積む (Issue #882)。保存 / キャンセルは右端に残す。
            */}
            <div className="modal-footer-main">
              <div className="modal-footer-left">
                {/*
                 コンテンツ追加ボタン (カテゴリ別の 4 つ)。
                コンテンツが増えてもスクロールで見えなくならない。
                押すと末尾に追加され、BroadcastContentsEditor が件数増加を検知して
                追加したコンテンツを自動で開く。
              */}
                <div className="modal-footer-add" data-testid="footer-add-content">
                  <AddContentButtons onAdd={handleAddContent} />
                </div>
                {/*
                Issue #439: 放送地点まるごとプレビュー (通し聴き)。
                「開始チャイム → contents[] を並び順に → 終了チャイム」を実際の放送キュー順に
                連続再生する。個別プレビューでは絶対に気付けない
                「チャイムと本文の間が詰まりすぎ/空きすぎ」「コンテンツの並び順が変」を発見できる。

                押すと先に「プレビュー条件」ダイアログを開き、条件を選んでから再生に進む。
                指定できる条件が無い地点 (未設定 / 任意テキストのみ) はダイアログを挟まず直接再生する。
              */}
                {onPreviewSsml ? (
                  <div className="modal-footer-preview" data-testid="footer-point-preview">
                    <button
                      type="button"
                      data-testid="point-preview-button"
                      aria-label="この放送地点を通しで試聴"
                      disabled={pointPreviewPlaying || values.contents.length === 0}
                      title={
                        values.contents.length === 0
                          ? 'コンテンツを追加すると通しで試聴できます'
                          : undefined
                      }
                      onClick={() => {
                        if (pointSampleHasFields) setPointConditionOpen(true);
                        else void handlePreviewWholePoint();
                      }}
                    >
                      {pointPreviewPlaying ? '再生中...' : '🔊 この放送地点を通しで試聴'}
                    </button>
                  </div>
                ) : null}
              </div>
              {/*
              入力エラーのサマリ。
              「どの項目でエラーになったのか分かりにくい」という指摘への対応として、
              従来の 1 行表示に代えて **全件のクリック可能な一覧** を出す。
              行をクリックすると `revealIssue` が該当箇所の折りたたみ / タブを開き、
              入力欄までスクロール + フォーカスする。
              エラーが 1 件も無い保存失敗 (= API エラー) のときは従来どおり
              `error` の 1 行だけを表示する。
            */}
              {issues.length > 0 ? (
                <div
                  className="validation-error-summary"
                  role="alert"
                  data-testid="validation-error-summary"
                >
                  <div className="validation-error-summary__head">
                    ⚠ 入力エラーが {issues.length} 件あります（クリックすると該当項目へ移動します）
                  </div>
                  <ol className="validation-error-summary__list">
                    {issues.map((issue, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          className="validation-error-summary__item"
                          data-testid={`validation-error-item-${i}`}
                          onClick={() => revealIssue(issue)}
                        >
                          {issue.message}
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : error ? (
                <div
                  className="error-message"
                  role="alert"
                  style={{ margin: 0, textAlign: 'left' }}
                >
                  {error}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              data-testid="cancel-button"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              data-testid="save-button"
              className="primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner />
                  保存中...
                </>
              ) : (
                '保存'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* 地図ポップアップ */}
      {mapOpen ? (
        <MapPopup
          // 「地図表示画面が小さい」という指摘に対応して、編集モーダル本体と
          // 同じ大きさ・同じ位置に重ねて表示する (実寸は開いた時点で計測する)。
          modalRef={modalRef}
          onClose={() => setMapOpen(false)}
          editingLatitude={values.latitude}
          editingLongitude={values.longitude}
          editingRadiusMeters={values.radius_meters || DEFAULT_RADIUS}
          otherPoints={otherPoints.filter(
            // 自分自身は除外し、かつ現在編集中の routeDirection と一致するもののみを残す。
            // 後者は「複製」でコピー元と異なる線区/方面を選択したケースで、
            // 親から渡された otherPoints がコピー元路線のままになっているのを防ぐための
            // 防御的フィルタ (通常編集時は親側で既に絞り込み済みのため副作用なし)。
            (p) => p.spotId !== values.spotId && p.routeDirection === values.routeDirection,
          )}
          routeShapePoints={effectiveShapePoints}
          // 地図右上の「駅へ移動」ドロップダウン用。編集対象の線区 (コピー先を含む) の駅一覧。
          stations={routeStops?.[editingRouteId]}
          // 路線の線形を線区カラーで描く (線形データがある線区のみ。例: 横浜線)。
          routeLine={visibleRouteLine}
          routeLineColor={routeLineColor}
          // 新規作成で位置が未指定のうちは、対象路線の全体が見える縮尺で表示する。
          // 位置を指定した後 (再度開いた場合を含む) は従来どおりピン中心で表示する。
          fitToRoute={
            mode === 'create' && (values.latitude === undefined || values.longitude === undefined)
          }
          onLocationChange={handleLocationChange}
        />
      ) : null}

      {/* 登録 / 更新の実行確認・変更破棄の確認 (Issue #765) */}
      {confirmDialog}

      {/* プレースホルダのヘルプ (右上「？」ボタン) */}
      {helpOpen ? <PlaceholderHelpDialog onClose={() => setHelpOpen(false)} /> : null}

      {/*
        通し試聴の前に開く「プレビュー条件」ダイアログ。
        「試聴を開始」で閉じて再生に進み、以降の進行表示は下の `PointPreviewDialog` が引き継ぐ。
        再生が始まっている間は開かない (多重起動の防止)。
      */}
      {pointConditionOpen && pointSampleHasFields && !pointPreviewPlaying ? (
        <PointPreviewConditionDialog
          fields={pointSampleFields}
          sample={pointSample.shown}
          limits={pointSampleLimits}
          onChange={pointSample.update}
          onLineChange={pointSample.updateLine}
          onDirectionChange={pointSample.updateDirection}
          onStart={() => {
            setPointConditionOpen(false);
            void handlePreviewWholePoint();
          }}
          onCancel={() => setPointConditionOpen(false)}
        />
      ) : null}

      {/*
        通し試聴の進行状況ダイアログ。
        試聴ボタンはモーダル最下部にあり、押した後に増える表示 (カラオケ文面 / 進捗 / エラー) が
        スクロール領域の外へ出て気づきにくかったため、モーダルを重ねて画面中央に出す。
        再生中 or エラーが残っている間だけ開く:
          - 正常終了 / 停止 → playing=false かつ error=null になり自動で閉じる
          - 失敗 / 一部スキップ → error が残るので「閉じる」で閉じる
      */}
      {pointPreviewPlaying || pointPreviewError !== null ? (
        <PointPreviewDialog
          playing={pointPreviewPlaying}
          progress={pointPreviewProgress}
          karaoke={pointKaraoke.current}
          error={pointPreviewError}
          onStop={() => pointPreviewController.current?.abort()}
          onClose={() => setPointPreviewError(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 放送開始 / 終了チャイム セレクタ (要件 3・4)
// ---------------------------------------------------------------------------

interface ChimeSelectFieldProps {
  /** `'start'` = 放送開始チャイム / `'end'` = 放送終了チャイム。data-testid の接頭辞にも使う。 */
  role: 'start' | 'end';
  /** 表示ラベル (例: 「放送開始チャイム（任意）」) */
  label: string;
  /** 現在選択されている chime_id (空文字 = 「(なし)」= 鳴らさない) */
  value: string;
  /** チャイムマスタ一覧 (`GET /chimes`) */
  chimes: Chime[];
  /** この役割のチャイムを現在試聴処理中かどうか (ボタンの多重押下抑止) */
  previewing: boolean;
  /** 試聴機能が利用可能か (親が onPreviewChime を渡しているか) */
  canPreview: boolean;
  /** 選択変更時のハンドラ (空文字 = 「(なし)」) */
  onChange: (chimeId: string) => void;
  /** 試聴ボタン押下時のハンドラ */
  onPreview: () => void;
}

/**
 * 放送開始 / 終了チャイムの選択フィールド (要件 3・4)。
 *
 * - チャイムマスタ (`GET /chimes`) の一覧をセレクトボックスで提示し、
 *   「(なし)」を選ぶと当該タイミングのチャイムを鳴らさない (既存挙動と同一)。
 * - 選択済みの場合のみ「▶ 試聴」ボタンを有効化し、`GET /chimes/{chime_id}/audio`
 *   で取得した音源を再生できる。
 * - チャイムマスタが空 (未登録) の場合はセレクトを disabled にし、
 *   「チャイムマスタ画面で登録してください」と案内する
 *   (選択肢が無いのに操作できると迷わせるため)。
 */
function ChimeSelectField({
  role,
  label,
  value,
  chimes,
  previewing,
  canPreview,
  onChange,
  onPreview,
}: ChimeSelectFieldProps): React.JSX.Element {
  const hasChimes = chimes.length > 0;
  return (
    <label className="form-grid__group">
      <span>
        {label}
        {hasChimes ? (
          <HintTooltip label={`${label}の説明`} testId={`${role}-chime-hint`}>
            {role === 'start'
              ? 'この放送地点の放送キューの先頭に鳴らすチャイム音です。「（なし）」で鳴りません。'
              : 'この放送地点の放送キューの末尾に鳴らすチャイム音です。「（なし）」で鳴りません。'}
          </HintTooltip>
        ) : null}
      </span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          data-testid={`${role}-chime-select`}
          aria-label={label}
          value={value}
          disabled={!hasChimes}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">（なし / 鳴らさない）</option>
          {chimes.map((chime) => (
            <option key={chime.chime_id} value={chime.chime_id}>
              {chime.label ?? chime.chime_id}
            </option>
          ))}
        </select>
        {canPreview ? (
          <button
            type="button"
            data-testid={`${role}-chime-preview-button`}
            aria-label={`${label}を試聴`}
            title="選択したチャイム音を再生します"
            disabled={!value || previewing}
            onClick={onPreview}
          >
            {previewing ? '再生中...' : '▶ 試聴'}
          </button>
        ) : null}
      </div>
    </label>
  );
}

interface ChimeFieldsProps {
  startChimeId: string;
  endChimeId: string;
  chimes: Chime[];
  /** 現在試聴処理中の役割 (無ければ null) */
  previewing: 'start' | 'end' | null;
  canPreview: boolean;
  onStartChange: (chimeId: string) => void;
  onEndChange: (chimeId: string) => void;
  onPreview: (role: 'start' | 'end') => void;
}

/**
 * 放送開始 / 終了チャイムのセレクトを縦に並べるフィールド。
 *
 * 開始と終了は最初から別々のセレクト (`start-chime-select` / `end-chime-select`) で選ぶ。
 * チャイムマスタが未登録のときの案内は、2 つのセレクトで重複しないよう 1 か所にまとめて出す。
 */
function ChimeFields({
  startChimeId,
  endChimeId,
  chimes,
  previewing,
  canPreview,
  onStartChange,
  onEndChange,
  onPreview,
}: ChimeFieldsProps): React.JSX.Element {
  return (
    <div className="form-grid__group" data-testid="chime-fields">
      <ChimeSelectField
        role="start"
        label="放送開始チャイム（任意）"
        value={startChimeId}
        chimes={chimes}
        previewing={previewing === 'start'}
        canPreview={canPreview}
        onChange={onStartChange}
        onPreview={() => onPreview('start')}
      />
      <ChimeSelectField
        role="end"
        label="放送終了チャイム（任意）"
        value={endChimeId}
        chimes={chimes}
        previewing={previewing === 'end'}
        canPreview={canPreview}
        onChange={onEndChange}
        onPreview={() => onPreview('end')}
      />
      {chimes.length > 0 ? null : (
        // チャイムが選べない理由は「現在の状態」の案内なのでツールチップにせず常時表示する。
        <span className="hint" style={{ fontSize: 12, color: '#555' }}>
          チャイム音が未登録です。「チャイム音設定」画面で音源を登録すると選択できます。
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 地図ポップアップ
// ---------------------------------------------------------------------------

interface MapPopupProps {
  /**
   * 編集モーダル本体への ref。
   * ポップアップを「モーダルと同じ大きさ・同じ位置」に重ねるために、開いた時点で
   * `getBoundingClientRect()` により実寸を計測する。
   */
  modalRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  editingLatitude: number | undefined;
  editingLongitude: number | undefined;
  editingRadiusMeters: number;
  otherPoints: BroadcastPoint[];
  routeShapePoints: RouteShapePointsFile | null;
  /** 編集対象線区の駅一覧 (地図右上の駅ジャンプ用) */
  stations?: RouteStop[];
  /** 編集対象線区の路線線形 (線区カラーで描画) */
  routeLine?: RouteLineFile | null;
  /** 路線線形の描画色 (線区カラー) */
  routeLineColor?: string;
  /** 対象路線の全体が見える縮尺で表示するか (新規作成で位置未指定のとき true) */
  fitToRoute?: boolean;
  onLocationChange: (lat: number, lng: number) => void;
}

function MapPopup({
  modalRef,
  onClose,
  editingLatitude,
  editingLongitude,
  editingRadiusMeters,
  otherPoints,
  routeShapePoints,
  stations,
  routeLine,
  routeLineColor,
  fitToRoute,
  onLocationChange,
}: MapPopupProps): React.JSX.Element {
  /**
   * ポップアップの位置 + サイズ。
   * 初期値はモーダル本体の実寸 (= 同じ大きさ・同じ位置) で、以降ヘッダーの
   * ドラッグやウィンドウ端の `resize: both` でユーザーが自由に変更できる。
   */
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  // ドラッグ中かどうかを管理する ref (state にすると再レンダリングが多発するため ref を使う)
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);

  /**
   * 初期サイズ・位置: 編集モーダル本体と同じ矩形に合わせる。
   *
   * 従来は「地図を開く」ボタン直下に 520px 幅の小さなポップアップを出していたが、
   * 地図が狭くて位置を選びにくいという指摘があったため、モーダルと同サイズに変更した。
   * モーダルがまだ計測できない (幅 0 = jsdom 等) 場合はビューポート基準の
   * フォールバック値を使う。
   */
  useEffect(() => {
    const modalRect = modalRef.current?.getBoundingClientRect();
    if (modalRect && modalRect.width > 0 && modalRect.height > 0) {
      setRect({
        top: modalRect.top,
        left: modalRect.left,
        width: modalRect.width,
        height: modalRect.height,
      });
      return;
    }
    setRect(fallbackPopupRect());
  }, [modalRef]);

  // Esc キーで地図を閉じる (座標は入力欄に残る)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // ドラッグ開始
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // ヘッダー内のボタンはドラッグ対象外
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    if (rect === null) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect.left,
      origTop: rect.top,
    };
  };

  // ドラッグ中 (window に登録して要素外でも追従)
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const newLeft = Math.max(0, dragRef.current.origLeft + dx);
      const newTop = Math.max(0, dragRef.current.origTop + dy);
      setRect((r) => (r ? { ...r, left: newLeft, top: newTop } : r));
    };

    const handleMouseUp = (): void => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    top: rect?.top ?? -9999,
    left: rect?.left ?? -9999,
    width: rect?.width ?? 0,
    height: rect?.height ?? 0,
    zIndex: 1100,
    resize: 'both',
    overflow: 'hidden',
    // 初期サイズ・位置が決まるまで非表示
    visibility: rect ? 'visible' : 'hidden',
  };

  /** 位置が指定済みか (緯度・経度の両方が確定しているか) */
  const hasLocation = editingLatitude !== undefined && editingLongitude !== undefined;

  return (
    <div className="map-popup" style={popupStyle} role="dialog" aria-label="地図で位置を選択">
      <div className="map-popup__header" onMouseDown={handleMouseDown} style={{ cursor: 'grab' }}>
        <span className="map-popup__title">📍 地図で位置を選択</span>
        <button
          type="button"
          className="map-popup__close"
          aria-label="地図を閉じる"
          title="地図を閉じます（選択した座標は入力欄に残ります）"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {/* 操作方法のヒント。座標が即時反映されることを明示する */}
      <p className="map-popup__hint">
        地図をクリック、またはピンをドラッグして位置を指定してください（座標は入力欄へ即時反映されます）。右クリックメニューで選ぶと、その位置に決めて地図を閉じます
      </p>
      <div className="map-popup__body">
        <BroadcastPointsMap
          editingLatitude={editingLatitude}
          editingLongitude={editingLongitude}
          editingRadiusMeters={editingRadiusMeters}
          otherPoints={otherPoints}
          routeShapePoints={routeShapePoints}
          stations={stations}
          routeLine={routeLine}
          routeLineColor={routeLineColor}
          fitToRoute={fitToRoute}
          onLocationChange={onLocationChange}
          // 右クリック →「この位置を放送地点の位置にする」。編集中の地点の座標をセットして
          // 地図を閉じる (右クリックメニューで選ぶのは位置の確定操作のため。座標は入力欄に残り、
          // 保存は行わない)。左クリック / ドラッグでの移動は従来どおり地図を開いたまま。
          onRegisterAtLocation={(lat, lng) => {
            onLocationChange(lat, lng);
            onClose();
          }}
          contextMenuLabel="この位置を放送地点の位置にする"
        />
      </div>
      {/*
        フッター。
        従来は「× で閉じる」しか選択肢が無く、位置を選んだ後どうすればよいのか
        分かりにくかった。現在の選択座標と「この位置を使う」ボタンを常時表示して
        次の操作を明示する (押しても保存は行われず、地図を閉じるだけ)。
      */}
      <div className="map-popup__footer">
        {hasLocation ? (
          <span className="map-popup__coords" data-testid="map-popup-coords">
            緯度 {editingLatitude?.toFixed(6)} / 経度 {editingLongitude?.toFixed(6)}
          </span>
        ) : (
          <span
            className="map-popup__coords map-popup__coords--empty"
            data-testid="map-popup-coords"
          >
            未指定（地図をクリックして位置を選んでください）
          </span>
        )}
        <button
          type="button"
          className="primary"
          data-testid="map-popup-confirm-button"
          aria-label="この位置を使う"
          title="地図で指定した緯度・経度を入力欄に反映して地図を閉じます（データの保存は編集画面の「保存」ボタンで行います）"
          disabled={!hasLocation}
          onClick={onClose}
        >
          この位置を使う
        </button>
      </div>
    </div>
  );
}

/** 言語別話者が未提供のときの共有空辞書 (Context の再レンダーを抱えないため定数化)。 */
const EMPTY_VOICE_SELECTIONS: VoiceSelections = {};

/**
 * コンテンツの有効 / 無効を放送地点の `enabled` へ連動させる (Issue #810)。
 *
 * 全コンテンツが無効なら地点も無効 (`enabled: false`)、一つでも有効なら地点も有効
 * (`enabled` キーを持たない)。コンテンツが 0 件のときは現在の地点の状態を維持する。
 */
export function withSyncedSpotEnabled(v: BroadcastPointFormValues): BroadcastPointFormValues {
  const spotEnabled = deriveSpotEnabled(v.contents, v.enabled !== false);
  const rest = { ...v };
  delete rest.enabled;
  return spotEnabled ? rest : { ...rest, enabled: false };
}

function normalize(v: BroadcastPointFormValues): BroadcastPointFormValues {
  return {
    routeDirection: v.routeDirection,
    spotId: v.spotId,
    description: v.description ?? '',
    latitude: v.latitude,
    longitude: v.longitude,
    radius_meters: v.radius_meters || DEFAULT_RADIUS,
    broadcast_delay_seconds: v.broadcast_delay_seconds ?? 0,
    // voice_id もフォーム内では常に string 保持 (未設定 = 空文字 =
    // サーバー側デフォルト Takumi にフォールバック)。保存時に親側で空文字→undefined に正規化する。
    voice_id: typeof v.voice_id === 'string' ? v.voice_id : '',
    // Issue #639: 日本語以外の言語別話者。フォーム内では常に辞書 (未設定 = 空辞書) で扱う。
    voice_by_language:
      v.voice_by_language && typeof v.voice_by_language === 'object'
        ? { ...v.voice_by_language }
        : {},
    // 要件 3・4: 放送開始 / 終了チャイム。未設定は空文字 (= 鳴らさない)。
    start_chime_id: typeof v.start_chime_id === 'string' ? v.start_chime_id : '',
    end_chime_id: typeof v.end_chime_id === 'string' ? v.end_chime_id : '',
    // Issue #679: 無効 (false) のときだけ保持する。未指定なら属性自体を作らない。
    ...(v.enabled === false ? { enabled: false } : {}),
    contents: Array.isArray(v.contents) ? v.contents : [],
  };
}

/**
 * フォームを開いた直後に「地図で位置を選択」ポップアップを自動で開くか。
 *
 * - 新規作成で座標が未指定 (「＋ 新規作成」ボタン / 複製): 開く (まず位置を選んでもらう)。
 * - 新規作成で座標が指定済み (地図の右クリック →「放送地点を新規作成」): 開かない。
 *   地図で位置を選び終えているため、再度の位置選択を求めない。
 * - 編集: 開かない。
 */
export function shouldOpenMapOnStart(
  mode: 'create' | 'edit',
  initialValue: Pick<BroadcastPointFormValues, 'latitude' | 'longitude'>,
): boolean {
  if (mode !== 'create') return false;
  const { latitude, longitude } = initialValue;
  const hasCoordinates =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude);
  return !hasCoordinates;
}

/**
 * 初期値を正規化し、無効な地点のコンテンツを無効にそろえ、接続線の線区愛称の空欄を線区マスタの名称で埋める。
 * 画面を開いたとき / 比較用の「元の値」の生成に使う。
 */
function prepareValues(
  v: BroadcastPointFormValues,
  routes: RouteMaster[] | undefined,
): BroadcastPointFormValues {
  const normalized = normalize(v);
  const contents = fillContentsRouteNicknames(
    disableContentsOfDisabledSpot(normalized.contents, normalized.enabled !== false),
    routes,
  );
  return contents === normalized.contents ? normalized : { ...normalized, contents };
}

/**
 * 緯度経度から駅間ラベル (「開始駅-終了駅」) を判定するピュア関数。
 * - 緯度経度が undefined / NaN の場合や、駅順マスタが空の場合は null。
 * - 該当する駅間が判定できない場合も null。
 *
 * BroadcastPointForm の自動入力ロジックから抽出してテスト可能にしたもの。
 */
export function computeStationSegmentLabel(
  routeStops: Record<string, RouteStop[]> | undefined,
  routeDirection: string,
  latitude: number | undefined,
  longitude: number | undefined,
): string | null {
  if (
    latitude === undefined ||
    longitude === undefined ||
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  ) {
    return null;
  }
  const { routeId } = parseRouteDirection(routeDirection);
  const segment = findStationSegment(routeStops?.[routeId], latitude, longitude);
  const label = formatStationSegmentLabel(segment);
  return label || null;
}

/**
 * 入力値のバリデーション。エラー文言を返す (null = 問題なし)。
 *
 * **後方互換のための薄いラッパー**。内部で全件収集する {@link validateIssues} を呼び、
 * 先頭 issue の `message` を返す。文言は従来と完全に同一なので既存テストは通る。
 *
 * 新規コードは「どの項目でエラーになったか」を UI で案内できる
 * {@link validateIssues} を使うこと。
 */
export function validate(values: BroadcastPointFormValues): string | null {
  return firstIssueMessage(validateIssues(values));
}

/**
 * 入力値のバリデーションエラーを **すべて** 収集する。
 *
 * 従来の `validate()` は最初の 1 件で early return していたため、ユーザーは
 * 「保存 → エラー 1 件表示 → 直す → 保存」を何度も繰り返す必要があった。
 * さらにエラーメッセージだけでは **どの入力欄が原因なのか画面上で特定できない**
 * (コンテンツはアコーディオン、接続線・方面はタブで隠れている) という問題もあった。
 *
 * 本関数は全件を `ValidationIssue[]` として返し、各 issue に「該当入力欄の
 * `data-testid`」と「折りたたみ / タブを開くための index 群」を持たせる。
 * これにより UI 側で
 *   - エラー一覧をクリック可能なサマリとして提示する
 *   - クリック時に該当箇所を自動展開してスクロール + フォーカスする
 *   - 該当欄に赤枠 (`.is-invalid`) を付ける
 * ことができる。
 *
 * 地点の基本情報 (説明 / 緯度 / 経度 / 半径 / 放送遅延) は **常にフォーム先頭に\n * 表示されている** ので `contentIndex` は付けず、`testId` のみを設定する。
 *
 * `message` は従来の文言と 1 文字も変えていない (回帰防止)。
 */
export function validateIssues(values: BroadcastPointFormValues): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  /** 地点の基本情報エラーを 1 件積む */
  const push = (message: string, testId?: string): void => {
    issues.push({ message, target: testId !== undefined ? { testId } : {} });
  };

  if (!values.routeDirection || !values.routeDirection.includes('#')) {
    // routeDirection はモーダルのタイトルに出るだけで入力欄が無いため testId なし。
    push('routeDirection は "<線区>#<方面>" 形式で必須です');
  }
  if (!values.description || values.description.trim() === '') {
    push('説明を入力してください', 'description-input');
  }
  // 緯度・経度の入力欄は画面に出さず、地図で位置を指定する運用のため、
  // 緯度/経度を別々に報告せず「位置」の 1 件にまとめる。エラーの飛び先は地図ボタン。
  // 「未指定」と「範囲外」は排他で報告する (未指定のときに範囲チェックを重ねない)。
  const latitudeMissing = values.latitude === undefined || Number.isNaN(values.latitude);
  const longitudeMissing = values.longitude === undefined || Number.isNaN(values.longitude);
  if (latitudeMissing || longitudeMissing) {
    push('地図で位置を指定してください', 'map-open-button');
  } else if (
    (values.latitude as number) < -90 ||
    (values.latitude as number) > 90 ||
    (values.longitude as number) < -180 ||
    (values.longitude as number) > 180
  ) {
    // 地図の操作では起きないが、インポート値などで範囲外になった場合の防御。
    push('地図で位置を指定し直してください（指定された位置が範囲外です）', 'map-open-button');
  }
  if (values.radius_meters <= 0) {
    push('半径は 1 メートル以上で指定してください', 'radius-input');
  }
  if (values.broadcast_delay_seconds < 0) {
    push('放送遅延は 0 以上で指定してください', 'broadcast-delay-input');
  }
  // コンテンツ毎のバリデーション (こちらも全件収集する)
  for (let i = 0; i < values.contents.length; i++) {
    issues.push(...validateContentIssues(values.contents[i] as BroadcastContent, i));
  }
  return issues;
}
